import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Container,
  Grid,
  Card,
  Text,
  Title,
  Group,
  Button,
  RingProgress,
  SimpleGrid,
  Badge,
  Stack,
  Box,
  Progress,
  ScrollArea
} from '@mantine/core'
import {
  IconClock,
  IconBook,
  IconAlertCircle
} from '@tabler/icons-react'
import { notifications } from '@mantine/notifications'
import { useAuth } from '../contexts/AuthContext'
import { enrollmentService, courseService } from '../services'
import { supabase } from '../lib/supabase'
import CourseCard from '../components/CourseCard'


function Dashboard() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [dashboardData, setDashboardData] = useState({
    currentProgress: 0,
    avgCompetency: null,
    totalCourses: 0,
    coursesCompleted: 0,
    currentCourse: null,
    enrolledCourses: [],
    coursesNeedingAttention: [],
    totalTimeSpent: 0,
    userData: null
  })

  useEffect(() => {
    const fetchDashboardData = async () => {
      if (!user?.email) return
      
      setLoading(true)
      try {
        // Fetch assigned courses with full details
        const assignedCourses = await courseService.getLearnerAssignedCourses(user.email)

        // Fetch enrollment data to get missing fields (overall_accuracy, cycles_to_mastery, total_time_seconds)
        const { data: enrollmentData } = await supabase
          .from('enrollments')
          .select('course_id, overall_accuracy, cycles_to_mastery, total_time_seconds')
          .eq('user_email', user.email)

        // Merge enrollment data with assigned courses
        const enrichedCourses = assignedCourses.map(course => {
          const enrollment = enrollmentData?.find(e => e.course_id === course.course_id)
          return {
            ...course,
            overall_accuracy: enrollment?.overall_accuracy || null,
            cycles_to_mastery: enrollment?.cycles_to_mastery || null,
            total_time_seconds: enrollment?.total_time_seconds || 0
          }
        })

        // Transform enriched courses to match dashboard needs
        const enrollments = enrichedCourses.map(course => ({
          id: course.course_id,
          title: course.title,
          instructor: course.instructor || course.organization_name || course.assigned_by || 'Your Organization',
          thumbnail: course.thumbnail_url || course.thumbnail || null,
          thumbnail_url: course.thumbnail_url || course.thumbnail || null,
          level: course.difficulty?.charAt(0).toUpperCase() + course.difficulty?.slice(1) || 'Intermediate',
          difficulty: course.difficulty,
          students: course.learner_count || 0,
          modules: course.question_count || 0,
          duration: course.total_time_seconds && course.total_time_seconds > 0
            ? `${Math.floor(course.total_time_seconds / 3600)}h ${Math.floor((course.total_time_seconds % 3600) / 60)}m`
            : 'Not started',
          rating: 0,
          category: course.category,
          description: course.description,
          progress: course.progress || 0,
          status: course.enrollment_status,
          isEnrolled: course.enrollment_status === 'enrolled' || course.enrollment_status === 'in_progress' || course.enrollment_status === 'completed',
          assignedBy: course.assigned_by,
          assignedAt: course.assigned_at,
          enrolledAt: course.enrolled_at,
          lastActivity: course.last_activity,
          totalTimeSpent: course.total_time_seconds || 0,
          enrollmentStatus: course.enrollment_status,
          organizationName: course.organization_name,
          overallAccuracy: course.overall_accuracy || null,
          cyclesToMastery: course.cycles_to_mastery || null
        }))
        
        // Calculate TRUE competency (average of overall_accuracy from completed courses)
        const completedWithAccuracy = enrollments?.filter(e => e.overallAccuracy !== null) || []
        const avgCompetency = completedWithAccuracy.length > 0
          ? completedWithAccuracy.reduce((sum, e) => sum + e.overallAccuracy, 0) / completedWithAccuracy.length
          : null

        // Calculate overall progress (completion rate)
        const totalProgress = enrollments?.reduce((sum, e) => sum + (e.progress || 0), 0) || 0
        const avgProgress = enrollments?.length > 0 ? totalProgress / enrollments.length : 0

        // Get current course (most recently accessed)
        const sortedEnrollments = enrollments?.sort((a, b) => 
          new Date(b.lastActivity || b.enrolledAt || 0) - new Date(a.lastActivity || a.enrolledAt || 0)
        )
        const currentEnrollment = sortedEnrollments?.[0]
        
        // Calculate total time spent
        const totalTimeSpent = enrollments?.reduce((sum, e) => sum + (e.totalTimeSpent || 0), 0) || 0

        // Identify courses needing attention (low accuracy or incomplete)
        const coursesNeedingAttention = enrollments?.filter(e =>
          (e.overallAccuracy !== null && e.overallAccuracy < 70) ||
          (e.status === 'in_progress' && e.progress < 100)
        ) || []
        
        setDashboardData({
          currentProgress: Math.round(avgProgress),
          avgCompetency: avgCompetency !== null ? Math.round(avgCompetency) : null,
          totalCourses: enrollments?.length || 0,
          coursesCompleted: enrollments?.filter(e => e.status === 'completed' || e.progress >= 100).length || 0,
          currentCourse: currentEnrollment,
          enrolledCourses: sortedEnrollments || [],
          coursesNeedingAttention,
          totalTimeSpent,
          userData: {
            name: user?.name || user?.email?.split('@')[0] || 'Learner',
            email: user?.email,
            avatar: user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'User')}&background=3B82F6&color=fff`
          }
        })
      } catch (error) {
        notifications.show({
          title: 'Error',
          message: 'Failed to load dashboard data',
          color: 'red'
        })
        // Set empty data on error
        setDashboardData({
          currentProgress: 0,
          avgCompetency: null,
          totalCourses: 0,
          coursesCompleted: 0,
          currentCourse: null,
          enrolledCourses: [],
          coursesNeedingAttention: [],
          totalTimeSpent: 0,
          userData: {
            name: user?.name || user?.email?.split('@')[0] || 'Learner',
            email: user?.email,
            avatar: user?.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.name || user?.email || 'User')}&background=3B82F6&color=fff`
          }
        })
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [user])

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Card p="xl" radius="md">
          <Text ta="center" c="dimmed">Loading your dashboard...</Text>
        </Card>
      </Container>
    )
  }

  return (
    <Container size="xl" py="xl">
      {/* Welcome Section */}
      <Box mb="xl">
        <Title order={2}>Welcome back, {dashboardData.userData?.name || 'Learner'}</Title>
      </Box>

      <Grid gutter="lg">
        {/* Left Column */}
        <Grid.Col span={{ base: 12, md: 8 }}>
          {/* Competency Status Card */}
          <Card radius="md" p="lg" mb="lg" style={{ backgroundColor: '#1e1e2e' }}>
            <Group justify="space-between" align="center">
              <Box>
                <Text c="white" size="sm" fw={500} mb={4}>
                  Your Competency
                </Text>
                {dashboardData.avgCompetency !== null ? (
                  <>
                    <Text c="white" size={36} fw={700} lh={1}>{dashboardData.avgCompetency}%</Text>
                    <Text size="xs" c="dimmed" mt={4}>
                      {dashboardData.avgCompetency >= 85 ? 'Proficient' :
                       dashboardData.avgCompetency >= 60 ? 'Competent' :
                       'Needs Attention'}
                    </Text>
                  </>
                ) : (
                  <>
                    <Text c="white" size={36} fw={700} lh={1}>--</Text>
                    <Text size="xs" c="dimmed" mt={4}>Complete assessments to see score</Text>
                  </>
                )}
              </Box>
              <RingProgress
                size={80}
                thickness={8}
                sections={[{
                  value: dashboardData.avgCompetency || 0,
                  color: dashboardData.avgCompetency >= 85 ? 'green' :
                         dashboardData.avgCompetency >= 60 ? 'orange' : 'red'
                }]}
                label={
                  <Text c="white" fw={600} ta="center" size="sm">
                    {dashboardData.avgCompetency !== null ? `${dashboardData.avgCompetency}%` : '--'}
                  </Text>
                }
              />
              <Box ta="center">
                <Text size={28} c="white" fw={600} lh={1.2}>{dashboardData.totalCourses}</Text>
                <Text size="xs" c="dimmed">Courses</Text>
              </Box>
            </Group>
          </Card>

          {/* Your Courses */}
          <Card radius="md" p="xl">
            <Group justify="space-between" mb="md">
              <Title order={4}>My Courses</Title>
            </Group>

            <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
              {dashboardData.enrolledCourses.length > 0 ? (
                dashboardData.enrolledCourses.slice(0, 4).map((course) => (
                  <CourseCard
                    key={course.id}
                    course={course}
                    user={user}
                    size="compact"
                  />
                ))
              ) : (
                <Card shadow="sm" radius="md" p="lg" style={{ gridColumn: '1 / -1' }}>
                  <Text ta="center" c="dimmed">No courses assigned yet</Text>
                  <Button
                    variant="light"
                    fullWidth
                    mt="md"
                    onClick={() => navigate('/courses')}
                  >
                    View Available Courses
                  </Button>
                </Card>
              )}
            </SimpleGrid>
          </Card>
        </Grid.Col>

        {/* Right Column */}
        <Grid.Col span={{ base: 12, md: 4 }}>
          {/* Courses Requiring Attention */}
          <Card radius="md" p="lg" mb="lg">
            <Group mb="md">
              <IconAlertCircle size={20} color="orange" />
              <Title order={4}>Requires Attention</Title>
            </Group>
            {dashboardData.coursesNeedingAttention.length > 0 ? (
              <ScrollArea h={250}>
                <Stack gap="sm">
                {dashboardData.coursesNeedingAttention.slice(0, 3).map((course) => (
                  <Card
                    key={course.id}
                    shadow="sm"
                    radius="md"
                    p="sm"
                    style={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/courses/${course.id}`)}
                  >
                    <Text size="sm" fw={500} lineClamp={1}>{course.title}</Text>
                    <Group gap="xs" mt="xs">
                      {course.overallAccuracy !== null && (
                        <Badge
                          size="sm"
                          color={course.overallAccuracy >= 70 ? 'orange' : 'red'}
                          variant="light"
                        >
                          {course.overallAccuracy}% Competency
                        </Badge>
                      )}
                      {course.progress < 100 && (
                        <Badge size="sm" color="gray" variant="light">
                          {course.progress}% Complete
                        </Badge>
                      )}
                    </Group>
                  </Card>
                ))}
                </Stack>
              </ScrollArea>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="md">
                All courses on track
              </Text>
            )}
          </Card>

          {/* Recent Activity */}
          <Card radius="md" p="lg" mb="lg">
            <Title order={4} mb="md">Recent Activity</Title>
            {dashboardData.currentCourse ? (
              <Card shadow="sm" radius="md" p="md">
                <Box
                  h={120}
                  mb="md"
                  style={{
                    backgroundImage: `url(${dashboardData.currentCourse.thumbnail || 'https://images.unsplash.com/photo-1516116216624-53e697fedbea?w=400'})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    borderRadius: '8px'
                  }}
                />
                <Group gap="xs" mb="xs">
                  <Badge
                    color={dashboardData.currentCourse.level === 'Beginner' ? 'gray' : dashboardData.currentCourse.level === 'Intermediate' ? 'orange' : 'red'}
                    variant="light"
                    size="sm"
                  >
                    {dashboardData.currentCourse.level}
                  </Badge>
                  {dashboardData.currentCourse.overallAccuracy !== null && (
                    <Badge
                      color={dashboardData.currentCourse.overallAccuracy >= 85 ? 'green' :
                             dashboardData.currentCourse.overallAccuracy >= 60 ? 'orange' : 'red'}
                      variant="filled"
                      size="sm"
                    >
                      {dashboardData.currentCourse.overallAccuracy}% Competency
                    </Badge>
                  )}
                </Group>
                <Title order={6} mb="xs" lineClamp={2}>
                  {dashboardData.currentCourse.title}
                </Title>
                <Progress value={dashboardData.currentCourse.progress || 0} mb="xs" size="sm" />
                <Group gap="xs" mb="sm">
                  <Group gap={4}>
                    <IconBook size={12} />
                    <Text size="xs" c="dimmed">{dashboardData.currentCourse.progress || 0}% Complete</Text>
                  </Group>
                  <Group gap={4}>
                    <IconClock size={12} />
                    <Text size="xs" c="dimmed">
                      {dashboardData.currentCourse.totalTimeSpent > 0
                        ? `${Math.floor(dashboardData.currentCourse.totalTimeSpent / 3600)}h ${Math.floor((dashboardData.currentCourse.totalTimeSpent % 3600) / 60)}m`
                        : 'Not started'}
                    </Text>
                  </Group>
                </Group>
                <Button
                  fullWidth
                  size="sm"
                  onClick={() => navigate(
                    dashboardData.currentCourse.progress === 100
                      ? `/assessment/${dashboardData.currentCourse.id}/complete`
                      : `/courses/${dashboardData.currentCourse.id}`
                  )}
                >
                  {dashboardData.currentCourse.progress === 100 ? 'View Results' : 'Continue Learning'}
                </Button>
              </Card>
            ) : (
              <Text size="sm" c="dimmed" ta="center" py="xl">
                No recent activity
              </Text>
            )}
          </Card>

          {/* Learning Stats */}
          <Card radius="md" p="lg">
            <Title order={4} mb="md">Your Metrics</Title>
            <Stack gap="md">
              <Box>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Courses Assigned</Text>
                  <Text size="sm" fw={500}>{dashboardData.totalCourses}</Text>
                </Group>
              </Box>

              <Box>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Courses Completed</Text>
                  <Text size="sm" fw={500} c="green">{dashboardData.coursesCompleted}</Text>
                </Group>
              </Box>

              <Box>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Average Competency</Text>
                  <Text
                    size="sm"
                    fw={500}
                    c={dashboardData.avgCompetency !== null
                      ? (dashboardData.avgCompetency >= 85 ? 'green' :
                         dashboardData.avgCompetency >= 60 ? 'orange' : 'red')
                      : 'dimmed'}
                  >
                    {dashboardData.avgCompetency !== null ? `${dashboardData.avgCompetency}%` : 'Not yet assessed'}
                  </Text>
                </Group>
              </Box>

              <Box>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Total Training Hours</Text>
                  <Text size="sm" fw={500}>
                    {dashboardData.totalTimeSpent > 0
                      ? `${Math.floor(dashboardData.totalTimeSpent / 3600)}h ${Math.floor((dashboardData.totalTimeSpent % 3600) / 60)}m`
                      : '0h 0m'}
                  </Text>
                </Group>
              </Box>

              <Box>
                <Group justify="space-between">
                  <Text size="sm" c="dimmed">Completion Rate</Text>
                  <Text size="sm" fw={500}>
                    {dashboardData.totalCourses > 0
                      ? `${Math.round((dashboardData.coursesCompleted / dashboardData.totalCourses) * 100)}%`
                      : '0%'}
                  </Text>
                </Group>
              </Box>

              <Button
                fullWidth
                size="lg"
                mt="md"
                onClick={() => navigate('/courses')}
              >
                View All Courses
              </Button>
            </Stack>
          </Card>
        </Grid.Col>
      </Grid>
    </Container>
  )
}


export default Dashboard